import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Shield, Check } from 'lucide-react';
import WhatsAppNumberForm from './WhatsAppNumberForm';
import { isPro as checkIsPro, getPlanLabel, getPlanBadgeClasses } from '../../utils/planPermissions';

// --- Badge del plan con shimmer para Premium ---
const PlanBadge: React.FC<{ plan: string }> = ({ plan }) => {
  const isPremium = plan === 'premium';
  return (
    <div className={`relative overflow-hidden px-2 py-0.5 text-xs font-medium rounded-full ${getPlanBadgeClasses(plan as any)}`}>
      {getPlanLabel(plan as any)}
      {isPremium && (
        <span className="absolute inset-0 animate-shimmer rounded-full" />
      )}
    </div>
  );
};

// Panel de bienvenida comprimido: saludo + plan + UNA línea de configuración.
// Las métricas vivas y las acciones del día viven en WeeklyPulsePanel /
// TodayActionsPanel; aquí solo queda identidad + avisos de cuenta.
const WelcomePanel: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [showWhatsAppForm, setShowWhatsAppForm] = useState(false);
  const [showThankYouMessage, setShowThankYouMessage] = useState(false);

  const loyaltyMode = currentUser?.loyaltyMode || 'visits';
  const cashbackPercentage = currentUser?.cashbackPercentage || 10;
  const pointsPerVisit = currentUser?.pointsPerVisit || 1;

  useEffect(() => {
    if (currentUser && !currentUser.phoneNumber) {
      setShowWhatsAppForm(true);
    } else {
      setShowWhatsAppForm(false);
    }
  }, [currentUser]);

  const handleWhatsAppFormClose = () => {
    setShowThankYouMessage(true);
    setShowWhatsAppForm(false);

    setTimeout(() => {
      setShowThankYouMessage(false);
    }, 5000);
  };

  const needsOnboarding = currentUser?.hasCompletedOnboarding === false;
  const missingConfiguration = !currentUser?.businessName || currentUser?.businessName === 'Mi Negocio';
  const hasProAccess = checkIsPro(currentUser?.subscription);

  // Con % por sucursal (8-oct-2026) se muestra el rango real: «2–8% de cashback».
  const pcts = (currentUser?.branches || []).filter(b => b.isActive !== false)
    .map(b => (b.cashbackPercentage ?? cashbackPercentage));
  const pMin = pcts.length ? Math.min(...pcts) : cashbackPercentage;
  const pMax = pcts.length ? Math.max(...pcts) : cashbackPercentage;
  const modeLabel = loyaltyMode === 'cashback'
    ? (pMin === pMax ? `${pMin}% de cashback` : `${pMin}–${pMax}% de cashback`)
    : `${pointsPerVisit} ${pointsPerVisit === 1 ? 'punto' : 'puntos'} por visita`;

  return (
    <div className="bg-surface rounded-lg shadow-md overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
      {/* WhatsApp Number Form */}
      {showWhatsAppForm && (
        <div className="px-6 py-4 border-b border-blue-100 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/10">
          <WhatsAppNumberForm onClose={handleWhatsAppFormClose} />
        </div>
      )}

      {/* Thank You Message */}
      {showThankYouMessage && (
        <div className="px-6 py-4 border-b border-green-100 dark:border-green-500/30 bg-green-50 dark:bg-green-500/10 animate-fade-in transition-all duration-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Check className="h-5 w-5 text-green-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800 dark:text-green-300">
                ¡Gracias por compartir tu número de WhatsApp!
              </p>
              <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                Solo lo utilizaremos para comunicaciones importantes relacionadas con tu cuenta.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Saludo + línea de configuración */}
      <div
        className="px-6 py-4"
        style={{ background: `linear-gradient(to right, rgb(var(--c-accent) / 0.08), rgb(var(--c-accent) / 0.16))` }}
      >
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xl md:text-3xl font-display font-extrabold tracking-tight text-content">¡Hola, {currentUser?.displayName || 'Usuario'}! 👋</h2>
          {hasProAccess && <PlanBadge plan={currentUser?.subscription?.plan || 'free'} />}
        </div>
        {!missingConfiguration && (
          <p className="text-sm md:text-base text-content-2 mt-1 truncate">
            <span className="font-semibold">{currentUser?.businessName}</span>
            <span className="text-content-3"> · </span>{modeLabel}
            {currentUser?.phoneNumber && (
              <>
                <span className="text-content-3"> · </span>
                <span className="text-content-3">WhatsApp {currentUser?.countryCode} {currentUser?.phoneNumber}</span>
              </>
            )}
          </p>
        )}
      </div>

      {/* Aviso de configuración pendiente */}
      {(needsOnboarding || missingConfiguration) && (
        <div className="px-6 py-4">
          <div className="bg-yellow-50 dark:bg-yellow-500/10 border-l-4 border-yellow-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0"><Shield className="h-5 w-5 text-yellow-400" /></div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  <span className="font-medium">Atención:</span> Es necesario completar la configuración de tu negocio para usar todas las funciones.
                </p>
                <div className="mt-2">
                  <button
                    onClick={() => navigate('/dashboard/business')}
                    className="text-sm font-medium text-yellow-700 dark:text-yellow-300 hover:text-yellow-600 dark:hover:text-yellow-400 underline"
                  >
                    Completar configuración
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WelcomePanel;
